import React__default from "react";
import { FloatingList } from "@floating-ui/react";
import { useScrollbarSize } from "@mihkeleidast/react-scrollbar-size";
import { useMediaQuery } from "react-responsive";
import { Container } from "../../container/container.mjs";
import "debounce";
import "tabbable";
import { useResponsiveProps } from "../../helpers/helper/hooks/use-responsive-props.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { CarouselContext } from "./carousel-context.mjs";
import { CarouselGrid } from "./carousel-grid.mjs";
import { CarouselNav } from "./carousel-nav.mjs";
const Carousel = (props) => {
  var _a;
  const { responsive, ...rest } = props;
  const { media } = useTheme();
  const shouldRenderSlider = useMediaQuery({
    minWidth: media.width[(_a = rest.breakPoint) != null ? _a : "lg"].min
  });
  const { width } = useScrollbarSize();
  if (responsive) {
    return /* @__PURE__ */ React__default.createElement(CarouselResponsive, { ...rest, responsive, shouldRenderSlider, scrollBarWidth: width });
  }
  return /* @__PURE__ */ React__default.createElement(CarouselInner, { ...rest, shouldRenderSlider, scrollBarWidth: width });
};
const CarouselResponsive = (props) => {
  const { responsive, ...rest } = props;
  const responsiveProps = useResponsiveProps({
    responsive
  });
  return /* @__PURE__ */ React__default.createElement(CarouselInner, { ...rest, ...responsiveProps });
};
const _CarouselInner = class _CarouselInner extends React__default.Component {
  constructor(props) {
    super(props);
    this.container = React__default.createRef();
    this.element = React__default.createRef();
    this.slidesRef = { current: [] };
    this.scrollTimeout = 0;
    this.autoplayInterval = 0;
    this.currentSlideIndex = 0;
    this.resizeObserver = null;
    this.containerWidth = 0;
    this.handleResize = () => {
      this.scrollToSlide(this.currentSlideIndex);
      this.handleScroll();
    };
    this.isSlideVisible = (index) => {
      const { slidesToShow } = this.propsWithDefaults;
      return index >= this.currentSlideIndex && index < this.currentSlideIndex + slidesToShow;
    };
    this.toggleAutoplay = () => {
      if (this.state.isAutoplaying) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };
    this.handleScroll = () => {
      const track = this.elementRef.current;
      if (!track) {
        return;
      }
      if (track.clientWidth === 0) {
        return;
      }
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      const nextScrollStart = track.scrollLeft <= 0;
      const nextScrollEnd = track.scrollLeft >= maxScrollLeft;
      if (this.state.isScrollStart !== nextScrollStart || this.state.isScrollEnd !== nextScrollEnd) {
        this.setState({
          isScrollStart: nextScrollStart,
          isScrollEnd: nextScrollEnd
        });
      }
      const nextSlideIndex = this.getSlideIndexFromScrollPosition(track.scrollLeft);
      if (nextSlideIndex !== void 0) {
        this.currentSlideIndex = nextSlideIndex;
      }
    };
    this.state = {
      isMounted: false,
      isScrollStart: true,
      isScrollEnd: true,
      isAutoplaying: false
    };
    this.scrollToSlide = this.scrollToSlide.bind(this);
  }
  get propsWithDefaults() {
    return this.props;
  }
  get elementRef() {
    return this.element;
  }
  componentDidMount() {
    const { initialSlide, activeItemIndex } = this.propsWithDefaults;
    this.setState(
      {
        isMounted: true
      },
      () => {
        if (activeItemIndex !== void 0) {
          this.scrollToSlide(activeItemIndex);
        } else {
          this.scrollToSlide(initialSlide);
        }
      }
    );
    this.handleScroll();
    if (this.props.autoplay) {
      this.startAutoplay();
    }
    if (this.container.current) {
      this.resizeObserver = new ResizeObserver((entries) => {
        requestAnimationFrame(() => {
          for (const entry of entries) {
            if (this.containerWidth !== entry.borderBoxSize[0].inlineSize) {
              this.containerWidth = entry.borderBoxSize[0].inlineSize;
              this.handleResize();
            }
          }
        });
      });
      this.resizeObserver.observe(this.container.current);
    }
  }
  componentDidUpdate(prevProps) {
    if (this.props.activeItemIndex !== prevProps.activeItemIndex && this.props.activeItemIndex !== void 0) {
      this.scrollToSlide(this.props.activeItemIndex);
    }
    this.handleScroll();
    if (prevProps.autoplay !== this.props.autoplay) {
      if (this.props.autoplay) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }
  }
  componentWillUnmount() {
    var _a;
    window.clearTimeout(this.scrollTimeout);
    this.stopAutoplay();
    (_a = this.resizeObserver) == null ? void 0 : _a.disconnect();
  }
  startAutoplay() {
    const { autoplaySpeed, slidesToScroll } = this.propsWithDefaults;
    if (!this.state.isAutoplaying) {
      this.autoplayInterval = window.setInterval(() => {
        const nextSlideIndex = Math.min(this.currentSlideIndex + slidesToScroll, this.slidesRef.current.length);
        if (!this.state.isScrollEnd) {
          this.scrollToSlide(nextSlideIndex);
        } else if (this.state.isScrollEnd && this.props.infinite) {
          this.scrollToSlide(0);
        } else {
          this.stopAutoplay();
        }
      }, autoplaySpeed);
      this.setState({ isAutoplaying: true });
    }
  }
  stopAutoplay() {
    window.clearInterval(this.autoplayInterval);
    this.setState({ isAutoplaying: false });
  }
  scrollToSlide(index) {
    const { align } = this.props;
    this.scrollTimeout = window.setTimeout(() => {
      const firstSlide = this.slidesRef.current[0];
      const slide = this.slidesRef.current[index];
      const slider = this.elementRef.current;
      if (firstSlide && slide && slider) {
        const nextLeft = align === "center" ? slide.offsetLeft - slider.offsetWidth / 2 + slide.offsetWidth / 2 : slide.offsetLeft - firstSlide.offsetLeft;
        slider.scrollTo({ behavior: "smooth", left: nextLeft });
        this.currentSlideIndex = index;
      }
    });
  }
  get hasContainer() {
    return this.props.hasContainer || !!this.props.fullWidth;
  }
  get isScrollable() {
    const { slidesToShow, children } = this.propsWithDefaults;
    return slidesToShow < React__default.Children.count(children);
  }
  get isSlider() {
    const { shouldRenderSlider, breakPoint } = this.propsWithDefaults;
    if (this.state.isMounted && (breakPoint === null || this.isScrollable && shouldRenderSlider)) {
      return true;
    }
    return false;
  }
  BEM() {
    const { slidesToShow, children, align, salesArgument, fullWidth, className, arrows, autoplay, centerArrows } = this.propsWithDefaults;
    const classArray = ["carousel"];
    if (this.isSlider) {
      classArray.push("carousel--scrollable");
      classArray.push("carousel--slides-".concat(slidesToShow));
      if (this.props.fade) {
        classArray.push("carousel--fade");
      }
      if (slidesToShow >= React__default.Children.count(children)) {
        classArray.push("carousel--center-slides");
      }
    }
    if (align) {
      classArray.push("carousel--align-".concat(align));
    }
    if (centerArrows) {
      if (this.isScrollable) {
        classArray.push("carousel--center-arrows-scrollable");
      }
      if (autoplay && this.isScrollable) {
        classArray.push("carousel--center-arrows-autoplay");
      }
    }
    if (arrows) {
      classArray.push("carousel--with-arrows");
    }
    if (salesArgument) {
      classArray.push("carousel--sales-argument");
    }
    if (this.hasContainer) {
      classArray.push("carousel--has-container");
    }
    if (fullWidth) {
      classArray.push("carousel--full-width");
      if (typeof fullWidth === "string") {
        classArray.push("carousel--full-width-".concat(fullWidth));
      } else if (typeof fullWidth !== "boolean") {
        classArray.push("carousel--full-width-left-".concat(fullWidth.left), "carousel--full-width-right-".concat(fullWidth.right));
      }
    }
    if (className) {
      classArray.push(className);
    }
    return classArray.join(" ");
  }
  handleArrowClick(event, direction) {
    const { slidesToScroll } = this.propsWithDefaults;
    event.preventDefault();
    this.stopAutoplay();
    if (direction === "next" && !this.state.isScrollEnd) {
      const nextSlideIndex = Math.min(this.currentSlideIndex + slidesToScroll, this.slidesRef.current.length);
      this.scrollToSlide(nextSlideIndex);
    } else if (!this.state.isScrollStart) {
      const prevSlideIndex = Math.max(this.currentSlideIndex - slidesToScroll, 0);
      this.scrollToSlide(prevSlideIndex);
    }
  }
  getSlideIndexFromScrollPosition(scrollLeft) {
    const { align } = this.props;
    const { isScrollStart } = this.state;
    const slides = this.slidesRef.current;
    const firstSlide = this.slidesRef.current[0];
    const slider = this.elementRef.current;
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (slider && slide && firstSlide) {
        const slideOffset = this.getSlideOffset(slide, firstSlide);
        const sliderOffset = this.getSliderOffset(slider, scrollLeft);
        if (slideOffset + 3 >= sliderOffset) {
          return align === "center" && isScrollStart ? i - 1 : i;
        }
      }
    }
  }
  getSlideOffset(slide, firstSlide) {
    const { align } = this.props;
    return align === "center" ? Math.max(slide.offsetLeft + slide.offsetWidth / 2, 0) : slide.offsetLeft - firstSlide.offsetLeft;
  }
  getSliderOffset(slider, scrollLeft) {
    const { align } = this.props;
    return align === "center" ? scrollLeft + slider.offsetWidth / 2 : scrollLeft;
  }
  renderSlider() {
    return /* @__PURE__ */ React__default.createElement(
      "section",
      {
        className: "carousel__slider",
        ref: (element) => {
          var _a, _b;
          (_b = (_a = this.props).sliderRef) == null ? void 0 : _b.call(_a, element);
          this.element.current = element;
        },
        onScroll: this.handleScroll,
        tabIndex: 0,
        "aria-labelledby": this.props["aria-labelledby"]
      },
      /* @__PURE__ */ React__default.createElement(FloatingList, { elementsRef: this.slidesRef }, this.props.children)
    );
  }
  renderGrid() {
    const { slidesToShow, children, showMoreButton, gridAlignment, align } = this.propsWithDefaults;
    return /* @__PURE__ */ React__default.createElement(CarouselGrid, { slidesToShow, showMoreButton, gridAlignment, align }, children);
  }
  renderMainContent() {
    const content = this.isSlider ? this.renderSlider() : this.renderGrid();
    if (this.hasContainer) {
      return /* @__PURE__ */ React__default.createElement(Container, { className: "carousel__container carousel__slider-container" }, content);
    }
    return content;
  }
  renderTopContent() {
    const { beforeSlidesContent, prevArrowText, nextArrowText, arrowStyle, autoplay, arrows } = this.props;
    const { isScrollStart, isScrollEnd, isAutoplaying } = this.state;
    const shouldShowCarouselNav = this.isSlider && this.isScrollable && (arrows || autoplay);
    const content = /* @__PURE__ */ React__default.createElement("div", { className: "carousel__top-content" }, beforeSlidesContent && /* @__PURE__ */ React__default.createElement("div", { className: "carousel__before-slides-content" }, beforeSlidesContent), shouldShowCarouselNav && /* @__PURE__ */ React__default.createElement(
      CarouselNav,
      {
        prevArrowText,
        nextArrowText,
        arrowStyle,
        isScrollStart,
        isScrollEnd,
        onPrevArrowClick: (event) => this.handleArrowClick(event, "prev"),
        onNextArrowClick: (event) => this.handleArrowClick(event, "next"),
        onPausePlayClick: this.toggleAutoplay,
        pausePlayIconType: isAutoplaying ? "pause" : "play",
        hasPausePlayButton: autoplay,
        hasArrows: arrows,
        centerArrows: this.props.centerArrows
      }
    ));
    if (!beforeSlidesContent && !shouldShowCarouselNav) {
      return null;
    }
    if (this.hasContainer) {
      return /* @__PURE__ */ React__default.createElement(Container, { className: "carousel__container" }, content);
    }
    return content;
  }
  renderBottomContent() {
    const content = /* @__PURE__ */ React__default.createElement("div", { className: "carousel__after-slides-content" }, this.props.afterSlidesContent);
    if (!this.props.afterSlidesContent) {
      return null;
    }
    if (this.hasContainer) {
      return /* @__PURE__ */ React__default.createElement(Container, { className: "carousel__container" }, content);
    }
    return content;
  }
  get contextValue() {
    return {
      scrollToSlide: this.scrollToSlide,
      isSlideVisible: this.isSlideVisible
    };
  }
  render() {
    const {
      activeItemIndex,
      beforeSlidesContent,
      afterSlidesContent,
      align,
      arrowStyle,
      arrows,
      autoplay,
      autoplaySpeed,
      breakPoint,
      centerArrows,
      children,
      className,
      fade,
      fullWidth,
      gridAlignment,
      hasContainer,
      infinite,
      initialSlide,
      nextArrowText,
      prevArrowText,
      responsive,
      showMoreButton,
      sliderRef,
      slidesToScroll,
      slidesToShow,
      salesArgument,
      shouldRenderSlider,
      scrollBarWidth,
      "aria-labelledby": ariaLabelledBy,
      ...rest
    } = this.propsWithDefaults;
    return /* @__PURE__ */ React__default.createElement(CarouselContext.Provider, { value: this.contextValue }, /* @__PURE__ */ React__default.createElement(
      "div",
      {
        ...rest,
        className: this.BEM(),
        "data-theme": salesArgument ? "dark" : void 0,
        ref: this.container,
        style: {
          "--carousel-scrollbar-width": "".concat(scrollBarWidth, "px")
        }
      },
      this.renderTopContent(),
      this.renderMainContent(),
      this.renderBottomContent()
    ));
  }
};
_CarouselInner.defaultProps = {
  arrows: true,
  autoplay: false,
  autoplaySpeed: 3e3,
  breakPoint: "lg",
  fade: true,
  hasContainer: false,
  infinite: false,
  initialSlide: 0,
  slidesToScroll: 1,
  slidesToShow: 4,
  centerArrows: false
};
let CarouselInner = _CarouselInner;
export {
  Carousel,
  CarouselInner
};
